import type { ItemMatriz, NivelCargo, PerfilImgr } from "@caint/shared";

/**
 * Leitura da "Matriz da Unidade" (roster esperado para o IMGR). Puro: recebe as
 * linhas das duas abas do Excel (Quantitativos + Matriz de Reporte) já como
 * arrays e devolve os itens validados. A conversão xlsx→arrays é feita por quem
 * chama (SheetJS no front), mantendo este módulo sem dependências.
 */

const NIVEIS: readonly NivelCargo[] = ["Estratégico", "Tático", "Operacional"];

/** Perfil do IMGR a partir do nível: Operacional = Liderado; demais = Gestor. */
export function perfilDoNivel(nivel: string): PerfilImgr {
  return nivel.trim() === "Operacional" ? "Liderado" : "Gestor";
}

export interface ResultadoMatriz {
  readonly itens: ItemMatriz[];
  readonly avisos: string[];
  readonly total: number; // soma dos quantitativos
}

function txt(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

/**
 * @param quantitativos linhas da aba "Quantitativos" (Cargo, Nível, Área, Quantitativo), com cabeçalho
 * @param reporte       linhas da aba "Matriz de Reporte" (Cargo, Nível, Reporta para), com cabeçalho
 */
export function parsearMatriz(
  quantitativos: readonly (readonly unknown[])[],
  reporte: readonly (readonly unknown[])[] = [],
): ResultadoMatriz {
  const reportaPor = new Map<string, string>();
  for (const linha of reporte) {
    const cargo = txt(linha[0]);
    if (!cargo || cargo.toLowerCase() === "cargo") {
      continue;
    }
    reportaPor.set(cargo.toUpperCase(), txt(linha[2]));
  }

  const itens: ItemMatriz[] = [];
  const avisos: string[] = [];
  const vistos = new Set<string>();
  for (const linha of quantitativos) {
    const cargo = txt(linha[0]);
    if (!cargo || cargo.toLowerCase() === "cargo") {
      continue; // cabeçalho ou linha vazia
    }
    const nivelRaw = txt(linha[1]);
    const area = txt(linha[2]);
    const quantitativo = Number(linha[3]);
    if (!(NIVEIS as readonly string[]).includes(nivelRaw)) {
      avisos.push(`"${cargo}": nível inválido (${nivelRaw || "vazio"}) — ignorado.`);
      continue;
    }
    if (!area) {
      avisos.push(`"${cargo}": área vazia — ignorado.`);
      continue;
    }
    if (!Number.isInteger(quantitativo) || quantitativo < 0) {
      avisos.push(`"${cargo}": quantitativo inválido (${txt(linha[3])}) — ignorado.`);
      continue;
    }
    const chave = cargo.toUpperCase();
    if (vistos.has(chave)) {
      avisos.push(`"${cargo}": cargo duplicado — mantida a 1ª ocorrência.`);
      continue;
    }
    vistos.add(chave);
    itens.push({
      cargo,
      nivel: nivelRaw as NivelCargo,
      area,
      perfil: perfilDoNivel(nivelRaw),
      reportaPara: reportaPor.get(chave) ?? "",
      quantitativo,
    });
  }

  return { itens, avisos, total: itens.reduce((s, i) => s + i.quantitativo, 0) };
}

/* ---------------- Vigência do quadro ---------------- */

/**
 * Depois de quantos meses o quadro passa a ser tratado como desatualizado. Um
 * ano é a régua prática: o quadro esperado da unidade muda com o contrato e com
 * o dimensionamento, e um quadro do ciclo passado distorce a cobertura do IMGR.
 */
export const MESES_VALIDADE_MATRIZ = 12;

export type StatusMatriz = "ausente" | "atualizado" | "desatualizado";

/** Meses cheios entre a data do quadro e agora (null se a data for inválida). */
export function mesesDesde(iso: string, agora: Date): number | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const meses = (agora.getFullYear() - d.getFullYear()) * 12 + (agora.getMonth() - d.getMonth());
  return Math.max(0, meses);
}

/** Sem quadro, dentro da validade, ou vencido (ver `MESES_VALIDADE_MATRIZ`). */
export function statusMatriz(iso: string | null | undefined, agora: Date): StatusMatriz {
  if (!iso) {
    return "ausente";
  }
  const meses = mesesDesde(iso, agora);
  if (meses === null) {
    return "ausente";
  }
  return meses >= MESES_VALIDADE_MATRIZ ? "desatualizado" : "atualizado";
}

/** Mês/ano em que o quadro entrou no sistema, no formato MM/AAAA. */
export function competenciaMatriz(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

/* ---------------- Modelo para download ---------------- */

/** Uma aba do modelo, já como linhas (o chamador vira isso em .xlsx). */
export interface AbaModeloMatriz {
  readonly nome: string;
  readonly linhas: readonly (readonly (string | number)[])[];
}

/** Nome sugerido do arquivo do modelo. */
export const NOME_MODELO_MATRIZ = "modelo-matriz-unidade.xlsx";

/**
 * Modelo da planilha aceita por `parsearMatriz`: mesmas abas, mesmas colunas e
 * na mesma ordem em que a leitura espera (a leitura é por posição, não por
 * nome da coluna — por isso o modelo é a documentação viva do formato).
 */
export function modeloMatriz(): readonly AbaModeloMatriz[] {
  return [
    {
      nome: "Quantitativos",
      linhas: [
        ["Cargo", "Nível", "Tipos de Áreas", "Quantitativo"],
        ["DIRETOR(A) GERAL", "Estratégico", "Direção", 1],
        ["COORDENADOR(A) DE ENFERMAGEM", "Tático", "Assistencial", 1],
        ["SUPERVISOR(A) ADMINISTRATIVO", "Tático", "Administrativa", 2],
        ["TÉCNICO(A) DE ENFERMAGEM", "Operacional", "Assistencial", 120],
        ["AUXILIAR DE SERVIÇOS GERAIS", "Operacional", "Apoio", 18],
      ],
    },
    {
      nome: "Matriz de Reporte",
      linhas: [
        ["Cargo", "Nível", "Reporta para Líderes"],
        ["DIRETOR(A) GERAL", "Estratégico", "Presidência EMSERH"],
        ["COORDENADOR(A) DE ENFERMAGEM", "Tático", "Diretor(a) Geral"],
        ["SUPERVISOR(A) ADMINISTRATIVO", "Tático", "Diretor(a) Geral"],
        ["TÉCNICO(A) DE ENFERMAGEM", "Operacional", "Coordenador(a) de Enfermagem"],
        ["AUXILIAR DE SERVIÇOS GERAIS", "Operacional", "Supervisor(a) Administrativo"],
      ],
    },
    {
      nome: "Instruções",
      linhas: [
        ["Modelo da Matriz da Unidade — CAINT"],
        [""],
        ["1. Substitua as linhas de exemplo pelo quadro real da unidade. Não apague o cabeçalho."],
        ["2. Não renomeie as abas: o sistema procura por 'Quantitativos' e 'Matriz de Reporte'."],
        ["3. Não mude a ordem das colunas — a leitura é por posição."],
        [""],
        ["Aba Quantitativos (obrigatória)"],
        ["Cargo", "texto; um cargo por linha, sem repetir (a 1ª ocorrência vale)"],
        ["Nível", "exatamente: Estratégico, Tático ou Operacional"],
        ["Tipos de Áreas", "texto livre (ex.: Direção, Assistencial, Administrativa, Apoio)"],
        ["Quantitativo", "número inteiro ≥ 0 — quantas pessoas a unidade tem naquele cargo"],
        [""],
        ["Aba Matriz de Reporte (opcional)"],
        ["Cargo", "mesmo nome usado na aba Quantitativos"],
        ["Nível", "informativo"],
        ["Reporta para Líderes", "cargo/instância a quem o cargo se reporta"],
        [""],
        ["O perfil do IMGR é calculado pelo sistema: Operacional = Liderado; Estratégico/Tático = Gestor."],
        ["Linhas fora dessas regras são ignoradas — a pré-visualização avisa quais e por quê."],
      ],
    },
  ];
}
